import { Link } from 'react-router-dom';
import { type ProfilePageData, telUrl, timeAgo, whatsappUrl } from '@astrologermatch/shared';
import { Avatar, Stars } from '../components/common.js';
import { SITE_NAME } from '../components/Layout.js';
import { beacon } from '../lib/api.js';
import { useSeo } from '../lib/seo.js';
import { useSettings } from '../lib/state.js';

// Ported from prototype/src/pages/PublicProfile.jsx.

export function ProfileUnavailable() {
  useSeo({ title: `Profile not available | ${SITE_NAME}`, robots: 'noindex,follow' });
  return (
    <div className="wrap page-pad">
      <div className="card narrow center-card">
        <h1 className="h2">Profile not available</h1>
        <p className="muted">This astrologer&apos;s page isn&apos;t published yet.</p>
        <Link to="/" className="btn btn-primary">
          Find an astrologer
        </Link>
      </div>
    </div>
  );
}

// ADR-005: the grace period ended without a verified business email. The URL keeps working —
// nothing is deleted, and verifying reactivates the listing instantly — so this is deliberately
// not the same "not published yet" message as ProfileUnavailable.
export function ProfileInactive() {
  useSeo({ title: `Profile inactive | ${SITE_NAME}`, robots: 'noindex' });
  return (
    <div className="wrap page-pad">
      <div className="card narrow center-card">
        <h1 className="h2">This profile is currently inactive</h1>
        <p className="muted">
          This astrologer&apos;s listing is temporarily inactive and isn&apos;t shown in search
          right now.
        </p>
        <Link to="/" className="btn btn-primary">
          Find an astrologer
        </Link>
      </div>
    </div>
  );
}

function contact(astrologerId: string, method: 'CALL' | 'WHATSAPP' | 'WEBSITE') {
  beacon('/api/v1/contacts', { astrologerId, method });
}

export function PublicProfile({ data }: { data: ProfilePageData }) {
  const settings = useSettings();
  const a = data.astrologer;
  const base = settings.siteUrl;
  const url = `${base}/astrologers/${a.slug}`;
  const reviews = data.reviews;
  const sameAs = [
    a.websiteUrl,
    a.googleBusinessUrl,
    a.facebookUrl,
    a.instagramUrl,
    ...a.otherLinks,
  ].filter((x): x is string => Boolean(x));

  useSeo({
    title: a.metaTitle,
    description: a.metaDescription,
    canonical: url,
    type: 'profile',
    ...(a.photoUrl
      ? { image: a.photoUrl.startsWith('http') ? a.photoUrl : base + a.photoUrl }
      : {}),
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ProfessionalService',
          '@id': `${url}#business`,
          name: a.practiceName ?? a.name,
          url,
          description: a.metaDescription,
          telephone: a.phone,
          ...(a.businessEmail ? { email: a.businessEmail } : {}),
          ...(a.photoUrl
            ? { image: a.photoUrl.startsWith('http') ? a.photoUrl : base + a.photoUrl }
            : {}),
          address: { '@type': 'PostalAddress', addressLocality: a.city, addressCountry: a.country },
          areaServed: a.consultsOnline ? 'Worldwide' : a.city,
          founder: {
            '@type': 'Person',
            name: a.name,
            jobTitle: 'Astrologer',
            ...(a.languages.length ? { knowsLanguage: a.languages } : {}),
          },
          knowsAbout: a.specialities,
          ...(sameAs.length ? { sameAs } : {}),
          ...(a.claimed && a.rating !== null && a.reviewCount > 0
            ? {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: a.rating,
                  reviewCount: a.reviewCount,
                  bestRating: 5,
                  worstRating: 1,
                },
                review: reviews.slice(0, 5).map((r) => ({
                  '@type': 'Review',
                  author: { '@type': 'Person', name: r.reviewerName },
                  datePublished: r.createdAt.slice(0, 10),
                  reviewRating: {
                    '@type': 'Rating',
                    ratingValue: r.rating,
                    bestRating: 5,
                    worstRating: 1,
                  },
                  reviewBody: r.text,
                })),
              }
            : {}),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: SITE_NAME, item: `${base}/` },
            { '@type': 'ListItem', position: 2, name: 'Astrologers', item: `${base}/` },
            { '@type': 'ListItem', position: 3, name: a.name, item: url },
          ],
        },
      ],
    },
  });

  const wa = whatsappUrl(a.whatsapp ?? a.phone);
  const tel = telUrl(a.phone);
  const total = reviews.length;

  return (
    <div className="public">
      <section className="public-hero">
        <nav className="wrap crumbs" aria-label="Breadcrumb">
          <Link to="/">{SITE_NAME}</Link>
          <span aria-hidden="true">›</span>
          <span>Astrologers</span>
          <span aria-hidden="true">›</span>
          <span aria-current="page">{a.name}</span>
        </nav>
        <div className="wrap public-hero-inner">
          <Avatar name={a.name} photoUrl={a.photoUrl} big />
          <div className="public-id">
            {a.claimed ? (
              <div className="verified-pill">✓ Verified astrologer</div>
            ) : (
              <div
                className="unclaimed-pill"
                title="This profile was created by AstrologerMatch from public information and hasn't been claimed by the astrologer yet"
              >
                Unclaimed
              </div>
            )}
            <h1>{a.name}</h1>
            <div className="public-biz">
              {a.practiceName ? `${a.practiceName} · ` : ''}Astrologer in {a.city || a.country}
            </div>
            <div className="public-meta">
              {a.rating !== null ? (
                <span>
                  <Stars value={a.rating} /> <b>{a.rating}</b> · {a.reviewCount} reviews
                </span>
              ) : (
                <span>New on {SITE_NAME}</span>
              )}
              <span>
                {a.city}, {a.country}
              </span>
              {a.experienceYears ? <span>{a.experienceYears} years&apos; experience</span> : null}
              {a.consultsOnline && <span>Online consultations</span>}
            </div>
          </div>
          <div className="public-cta">
            {!a.claimed && (
              <Link className="btn btn-gold" to={`/astrologers/${a.slug}/claim`}>
                Is this you? Claim this profile
              </Link>
            )}
            {tel && (
              <a
                className="btn btn-gold"
                href={tel}
                onClick={() => {
                  contact(a.id, 'CALL');
                }}
              >
                Call
              </a>
            )}
            {wa && (
              <a
                className="btn btn-light"
                href={wa}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  contact(a.id, 'WHATSAPP');
                }}
              >
                WhatsApp
              </a>
            )}
            {a.websiteUrl && (
              <a
                className="btn btn-light"
                href={a.websiteUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  contact(a.id, 'WEBSITE');
                }}
              >
                Website
              </a>
            )}
          </div>
        </div>
        {!a.claimed && (
          <div className="wrap">
            <Link to={`/astrologers/${a.slug}/remove`} className="tiny muted">
              Request removal
            </Link>
          </div>
        )}
      </section>

      <div className="wrap public-body">
        <div className="public-main">
          <section className="card">
            <h2 className="h3">About {a.name}</h2>
            <p className="pre-line">{a.aboutYou ?? 'No description yet.'}</p>
            <h3 className="label mt16">Specialities</h3>
            <div className="chips">
              {a.specialities.map((s) => (
                <span key={s} className="chip">
                  {s}
                </span>
              ))}
            </div>
            {a.languages.length > 0 && (
              <>
                <h3 className="label mt16">Languages</h3>
                <div className="chips">
                  {a.languages.map((s) => (
                    <span key={s} className="chip">
                      {s}
                    </span>
                  ))}
                </div>
              </>
            )}
          </section>

          {a.claimed && (
            <section className="card">
              <div className="split">
                <h2 className="h3">Client reviews</h2>
                <span className="tiny muted">Collected through one-time review links</span>
              </div>
              {reviews.length === 0 ? (
                <p className="muted">No reviews yet.</p>
              ) : (
                <ul className="review-list">
                  {reviews.map((r) => (
                    <li key={r.id}>
                      <div className="review-top">
                        <b>{r.reviewerName}</b>
                        <span className="tiny muted">{timeAgo(new Date(r.createdAt))}</span>
                      </div>
                      <Stars value={r.rating} />
                      <p className="pre-line">{r.text}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>

        <aside className="public-side">
          {a.claimed && (
            <section className="card">
              <h2 className="h3">Rating</h2>
              {total && a.rating !== null ? (
                <>
                  <div className="big-rating">
                    {a.rating}
                    <span>★</span>
                  </div>
                  <div className="tiny muted">{total} verified reviews</div>
                  <div className="dist">
                    {data.distribution.map((d) => (
                      <div key={d.stars} className="dist-row">
                        <span>{d.stars}★</span>
                        <span className="hbar-track">
                          {d.count > 0 && (
                            <span
                              className="hbar-fill gold"
                              style={{ width: `${String((d.count / total) * 100)}%` }}
                            />
                          )}
                        </span>
                        <span className="tiny muted">{d.count}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="muted">No ratings yet.</p>
              )}
            </section>
          )}
          {data.similar.length > 0 && (
            <section className="card similar">
              <h2 className="h3">Similar astrologers</h2>
              <ul className="similar-list">
                {data.similar.map((x) => (
                  <li key={x.slug}>
                    <Link to={`/astrologers/${x.slug}`} className="similar-row">
                      <Avatar name={x.name} photoUrl={x.photoUrl} />
                      <div className="similar-body">
                        <b>{x.name}</b>
                        <span className="tiny muted">
                          {x.city}, {x.country}
                          {x.rating !== null ? (
                            <>
                              {' · '}
                              <span className="stars">★</span> {x.rating} ({x.reviewCount})
                            </>
                          ) : (
                            ''
                          )}
                        </span>
                        <span className="similar-tags">
                          {x.sharedTags.map((t) => (
                            <span key={t} className="chip chip-soft">
                              {t}
                            </span>
                          ))}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="card">
            <h2 className="h3">Find your match</h2>
            <p className="small muted">
              Not sure this is the right astrologer for you? Tell our AI your problem and get
              matched in minutes.
            </p>
            <Link to="/" className="btn btn-primary btn-block">
              Talk to AI
            </Link>
          </section>
        </aside>
      </div>
    </div>
  );
}
